import { Switch } from '@mui/material';
import { isCommercialTermsProduct, isSubscriptionProduct } from '@weft/services';
import { BILLING_INTERVALS, PAYMENT_TERMS, PLAN_STATUSES } from '@weft/types';
import type { BillingInterval, Plan, ProductInput, PaymentTerm } from '@weft/types';
import { entries, isNotNullOrUndefined, outersect, withDefault } from '@weft/utils';
import cn from 'classnames';
import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path/eager';
import { twMerge } from 'tailwind-merge';
import type { PlanInputForm } from './CreatePlanForm/create-plan-form-types/plan-input-form';
import { PlanDurationAutoComplete } from './PlanDurationAutoComplete';
import { PlanSettingsSelect, type PlanSettingsSelectItem } from './PlanSettingsSelect';
import { TrialDurationAutoComplete } from './TrialDurationAutoComplete';

type PlanSettingsProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
  disabledFields?: FieldPath<Plan['billingData']>[];
  disableSettings?: boolean;

};

export const PlanSettings: React.FC<PlanSettingsProps> = ({ className, disabledFields, disableSettings, ...props }) => {
  const { control, watch, register, setValue, getValues, formState: { errors } } = useFormContext<PlanInputForm>();
  const plan = getValues();
  const isActive = plan.status === PLAN_STATUSES.Active;

  const onBillingIntervalChanged = useCallback((billingInterval: BillingInterval) => {
    setValue('billingData.billingInterval', billingInterval);
    const fixedProducts = withDefault(getValues('fixedProducts'), []);
    const filteredFixedProducts = fixedProducts.filter(product => !isCommercialTermsProduct(product) && !isSubscriptionProduct(product));
    const leftOverProducts = outersect(fixedProducts, filteredFixedProducts);

    const fixedProductsWithBillingInterval = filteredFixedProducts.map((product) => mapProductWithBillingInterval(product, billingInterval));

    if (isNotNullOrUndefined(fixedProductsWithBillingInterval)) {
      setValue('fixedProducts', [
        ...fixedProductsWithBillingInterval,
        ...leftOverProducts,
      ]);
    }
  }, []);

  const onPaymentTypeChanged = useCallback((paymentTerm: PaymentTerm) => {
    setValue('billingData.paymentTerm', paymentTerm);
    const fixedProducts = withDefault(getValues('fixedProducts'), []);

    const fixedProductsWithPaymentType = fixedProducts.map((product) => mapProductWithPaymentTerm(product, paymentTerm));

    if (isNotNullOrUndefined(fixedProductsWithPaymentType)) {
      setValue('fixedProducts', fixedProductsWithPaymentType);
    }
  }, []);

  const onActiveChanged = useCallback(() => {
    const newStatus = isActive ? PLAN_STATUSES.Inactive : PLAN_STATUSES.Active;
    setValue('status', newStatus);
  }, [isActive]);

  const { ref: billingIntervalRef, ...billingIntervalRegister } = register('billingData.billingInterval');
  const { ref: planTypeRef, ...planTypeRegister } = register('billingData.paymentTerm');

  const freeTrial = getValues('trial.free');

  return (
    <section
      className={twMerge('flex h-full flex-col px-2 gap-y-4', className)}
      {...props}
    >
      <span className='text-base font-medium'>
        Set Up Settings
      </span>

      <div className="flex h-full flex-col overflow-y-auto rounded-lg border-[#DFEBF1] bg-white py-8">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-y-8">
          <section className="flex flex-col gap-y-3">
            <label className="text-sm">Terms</label>
            {!disabledFields?.includes('billingInterval') && (
              <PlanSettingsSelect
                label='Billing interval'
                items={getBillingIntervalValues()}
                value={plan.billingData?.billingInterval}
                {...billingIntervalRegister}
                onChange={onBillingIntervalChanged}
                disabled={disableSettings}
                className={cn({ 'border border-input-error': errors.billingData?.billingInterval })}
                errorMessage={errors.billingData?.billingInterval?.message}
              />
            )}
            {!disabledFields?.includes('duration') && (
              <PlanDurationAutoComplete
                control={control}
                name="billingData.duration"
                defaultValue={plan.billingData?.duration}
                disabled={disableSettings}
              />
            )}

            {!disabledFields?.includes('paymentTerm') && (
              <PlanSettingsSelect
                label='Payment type'
                items={getPaymentTypeValues()}
                value={plan.billingData?.paymentTerm ?? PAYMENT_TERMS.Postpayment}
                {...planTypeRegister}
                onChange={onPaymentTypeChanged}
                disabled={disableSettings}
                className={cn({ 'border border-input-error': errors.billingData?.type }, 'h-[60px]')}
                errorMessage={errors.billingData?.type?.toString()}
              />
            )}

            <TrialDurationAutoComplete
              control={control}
              name="trial"
              disabled={disableSettings}
              freeTrial={freeTrial}
            />

            {isNotNullOrUndefined(watch('trial.duration')) && (
              <div className="border-line rounded-2lg flex h-16 items-center justify-between border p-5">
                <label>Free trial</label>
                <Controller
                  control={control}
                  name="trial.free"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      {...field}
                      disabled={disableSettings}
                    />
                  )}
                />
              </div>
            )}

            {!disabledFields?.includes('autoRenewal') && (
              <div className="border-line rounded-2lg flex h-16 items-center justify-between border p-5">
                <label>Auto renewal</label>
                <Controller
                  control={control}
                  name="billingData.autoRenewal"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      {...field}
                      disabled={disableSettings}
                    />
                  )}
                />
              </div>
            )}

            {!disabledFields?.includes('paymentTerm') && (
              <div className="border-line rounded-2lg flex h-16 items-center justify-between border p-5">
                <label>Active</label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Switch
                      checked={field.value === PLAN_STATUSES.Active}
                      {...field}
                      onChange={onActiveChanged}
                      disabled={disableSettings}
                    />
                  )}
                />
              </div>
            )}

            {!disabledFields?.includes('proRated') && (
              <div className="border-line rounded-2lg flex h-16 items-center justify-between border p-5">
                <label>Pro Rated</label>
                <Controller
                  control={control}
                  name="billingData.proRated"
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      {...field}
                      disabled={disableSettings}
                    />
                  )}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </section>
  );
};

function mapProductWithBillingInterval<ProductInputType extends ProductInput>(product: ProductInputType, planBillingInterval: BillingInterval): ProductInputType {
  if (isCommercialTermsProduct(product) && product.variants[0].pricing.priceConfiguration.shouldCreateSeparateInvoice) {
    return product;
  }

  return {
    ...product,
    billingInterval: planBillingInterval,
  };
}

function mapProductWithPaymentTerm<ProductInputType extends ProductInput>(product: ProductInputType, planPaymentTerm: PaymentTerm): ProductInputType {
  if (isCommercialTermsProduct(product) && product.variants[0].pricing.priceConfiguration.shouldCreateSeparateInvoice) {
    return product;
  }

  return {
    ...product,
    paymentTerm: planPaymentTerm,
  };
}

export function getBillingIntervalValues(): PlanSettingsSelectItem<BillingInterval>[] {
  const billingIntervals = {
    [BILLING_INTERVALS.Monthly]: 'Monthly',
    [BILLING_INTERVALS.BiMonthly]: 'Bi monthly',
    [BILLING_INTERVALS.Quarterly]: 'Quarterly',
    [BILLING_INTERVALS.SixMonths]: 'Every 6 months',
    [BILLING_INTERVALS.Yearly]: 'Yearly',
  };

  return mapObjectToSelectValues(billingIntervals);
}

export function getPaymentTypeValues(): PlanSettingsSelectItem<PaymentTerm>[] {
  const paymentTypes = {
    [PAYMENT_TERMS.Postpayment]: 'Post Pay',
    [PAYMENT_TERMS.Prepayment]: 'Pre Pay',
  };

  return mapObjectToSelectValues(paymentTypes);
}

function mapObjectToSelectValues<Item extends string | number>(object: Record<Item, string>): PlanSettingsSelectItem<Item>[] {
  const objectEntries = entries(object);

  return objectEntries.map(([value, label]) => ({
    value,
    label,
  }));
}